<template>
  <v-container
    grid-list-xl
    fluid
  >
    <LoadingState
      v-if="!product.initialized"
      :loading="loading"
      color="primary"
    />

    <v-layout
      v-else
      row
      wrap
    >
      <v-flex
        xs12
      >
        <v-alert
          :value="product.errors.length"
          type="error"
          icon="new_releases"
          dismissible
        >
          <div v-html="product.errorsText()"/>
        </v-alert>
      </v-flex>
      <v-flex
        v-if="!loading">
        <v-layout
          row
          wrap>
          <v-flex
            sm12
            lg3>
            <v-card>
              <v-list>
                <template v-for="tab in tabs">
                  <v-list-tile
                    v-if="!tab.hidden"
                    :key="tab.code"
                    @click="changeTab(tab.code)"
                  >
                    <v-list-tile-action>
                      <v-icon :color="currentTab === tab.code ? 'primary' : ''">
                        {{ tab.icon }}
                      </v-icon>
                    </v-list-tile-action>
                    <v-list-tile-title>
                      <span>{{ $t(tab.title) }}</span>
                    </v-list-tile-title>
                  </v-list-tile>

                  <v-divider
                    v-if="!tab.hidden"
                    :class="currentTab === tab.code ? 'primary' : ''"
                    :key="`divider-${tab.code}`"
                    class="bold-divider"
                    inset
                  />
                </template>
              </v-list>
            </v-card>
          </v-flex>

          <v-flex
            sm12
            lg9>
            <div v-if="currentTab === 'details'">
              <translations-tabs>
                <template #default="{language}">
                  <ProductSingleDescription
                    :product="product"
                    :language="language"
                    class="mb-4"
                  />
                </template>
              </translations-tabs>

              <ProductSingleOrganization
                :product="product"
                :edit-mode="editMode"
                class="mb-4"
                @select-taxonomy="dialog = true"
              />

              <ProductSingleApplication
                :product="product"
                class="mb-4"
              />
            </div>

            <div v-if="currentTab === 'pricing'">
              <ProductSinglePrice
                :product="product"
                :taxes="taxes"
                :settings="taxesSettings"
              />
            </div>

            <div v-if="currentTab === 'props'">
              <ProductSingleProperties :product="product"/>
            </div>

            <div v-if="currentTab === 'inventory'">
              <ProductSingleInventory :product="product"/>
            </div>

            <div v-if="currentTab === 'images'">
              <ProductSingleImages :product="product"/>
            </div>

            <div v-if="currentTab === 'extras'">
              <ProductSingleExtras :product="product"/>
            </div>

            <div v-if="currentTab === 'related'">
              <ProductSingleRelated :product="product"/>
            </div>

            <div v-if="currentTab === 'components-variants'">
              <ProductSingleOptions
                v-if="product.classification === 'common'"
                :product="product"
                class="mb-4"
              />
              <ProductSingleVariants
                v-if="product.classification === 'common'"
                v-model="selectedVariants"
                :product="product"
              />

              <ProductSingleComponents
                v-if="product.classification === 'bundle'"
                :product="product"
                @update-components="$emit('update-components', $event)"
                @remove-component="$emit('remove-component', $event)"
                @update-qty="$emit('update-qty', $event)"
              />
            </div>
          </v-flex>
        </v-layout>
      </v-flex>

      <v-flex
        v-if="!loading"
        xs12
      />
      <v-dialog
        v-model="dialog"
        persistent
        max-width="600px"
      >
        <div>
          <VWidget
            :title="$t('Select Taxonomy')"
          >
            <v-layout
              slot="widget-content"
              row
              wrap
            >
              <v-flex xs12>
                <v-treeview
                  :items="taxonomy"
                  :active.sync="active"
                  activatable
                  hoverable
                  active-class="primary--text"
                  return-object
                />
              </v-flex>
            </v-layout>
          </VWidget>
          <v-card>
            <v-card-actions>
              <v-spacer/>
              <v-btn
                color="darken-1"
                flat
                small
                @click="dialog = false"
              >
                {{ $t('Close') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </div>
      </v-dialog>
    </v-layout>
  </v-container>
</template>

<script>
import ProductSingleDescription from './ProductSingleDescription'
import ProductSingleImages from './ProductSingleImages'
import ProductSinglePrice from './ProductSinglePrice'
import ProductSingleInventory from './ProductSingleInventory'
import ProductSingleVariants from './ProductSingleVariants'
import ProductSingleOrganization from './ProductSingleOrganization'
import ProductSingleOptions from './ProductSingleOptions'
import ProductSingleProperties from './ProductSingleProperties'
import LoadingState from '@/components/LoadingState'
import ProductSingleApplication from '@/modules/Products/components/ProductSingle/ProductSingleApplication'
import ProductSingleComponents from '@/modules/Products/components/ProductSingle/ProductSingleComponents'
import ProductSingleExtras from '@/modules/Products/components/ProductSingle/ProductSingleExtras'
import ProductSingleRelated from '@/modules/Products/components/ProductSingle/ProductSingleRelated'
import TranslationsTabs from '@/components/widgets/TranslationsTabs'

export {
  ProductSingleDescription,
  ProductSingleImages,
  ProductSinglePrice,
  ProductSingleInventory,
  ProductSingleVariants,
  ProductSingleOrganization,
  ProductSingleOptions,
  ProductSingleProperties
}

export default {
  name: 'ProductSingle',

  components: {
    TranslationsTabs,
    ProductSingleRelated,
    ProductSingleExtras,
    ProductSingleComponents,
    ProductSingleApplication,
    LoadingState,
    ProductSingleDescription,
    ProductSingleImages,
    ProductSinglePrice,
    ProductSingleInventory,
    ProductSingleVariants,
    ProductSingleOrganization,
    ProductSingleOptions,
    ProductSingleProperties
  },

  props: {
    product: {
      type: Object,
      required: true
    },
    editMode: {
      type: Boolean,
      required: true
    }
  },

  data () {
    return {
      currentTab: 'details',
      active: [],
      taxonomy: [],
      taxes: [],
      taxesSettings: {},
      dialog: false,
      selectedVariants: []
    }
  },

  computed: {
    loading () {
      return Object.entries(this.product).length === 0 || !this.product.initialized
    },
    tabs () {
      return [
        {
          code: 'details',
          icon: 'settings',
          title: 'Details'
        },
        {
          code: 'pricing',
          icon: 'attach_money',
          title: 'Pricing'
        },
        {
          code: 'inventory',
          icon: 'dynamic_feed',
          title: 'Inventory'
        },
        {
          code: 'props',
          icon: 'list',
          title: 'Properties'
        },
        {
          code: 'images',
          icon: 'photo',
          title: 'Images'
        },
        {
          code: 'extras',
          icon: 'dynamic_feed',
          title: 'Extras'
        },
        {
          code: 'related',
          icon: 'account_tree',
          title: 'Related'
        },
        {
          code: 'components-variants',
          icon: 'insights',
          title: this.product.classification === 'bundle' ? 'Components' : 'Variants',
          hidden: this.product.classification === 'common' && this.product.parent
        }
      ]
    }
  },

  watch: {
    active: {
      handler: function (val) {
        if (val[0]) {
          const taxon = val[0]._attributes || val[0]
          this.$set(this.product, 'taxonomy', taxon)
          this.product.taxonomy_id = taxon.id
        }
      }
    }
  },

  async mounted () {
    this.taxesSettings = await this.$http.make('settings').find('general/taxes')
    this.taxes = await this.$http.make('vat').limit(-1).get()

    await this.$http.taxonomy
      .include(['children'])
      .fetch(items => {
        this.taxonomy = this.taxonomy.concat(items.filter(taxon => !taxon.parent_id))
      })
  },

  methods: {
    changeTab (tab) {
      this.currentTab = tab
    }
  }
}
</script>

<style lang="stylus">
.v-treeview--hoverable {
  .v-treeview-node__root:hover {
    cursor: pointer
  }
}

.bold-divider {
  max-height 2px;
}
</style>
